import { createContext, useContext, useState, useCallback, useMemo, ReactNode, Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { TagDataMap } from 'slate/extensions/tag/types';

export interface TagDisplayContextValue {
  tagLanguage: string;
  highlightedOption: string;
  visibilityOption: string[];
  extensionConceptIds: Set<string>;
  tagDataMap: TagDataMap;
  termHighlightTagIds: Set<string>;
  setTagLanguage: (lang: string) => void;
  setAvailableTagLanguages: (languages: string[] | undefined) => void;
  setHighlightedOption: Dispatch<SetStateAction<string>>;
  setVisibilityOption: Dispatch<SetStateAction<string[]>>;
  setExtensionConceptIds: Dispatch<SetStateAction<Set<string>>>;
  setTagDataMap: Dispatch<SetStateAction<TagDataMap>>;
  setTermHighlightTagIds: Dispatch<SetStateAction<Set<string>>>;
}

export const TagDisplayContext = createContext<TagDisplayContextValue>({} as TagDisplayContextValue);

const TAG_LANGUAGE_KEY = 'tagLanguageChoices';
const LEGACY_TAG_LANGUAGE_KEY = 'tagLanguage';

const writeTagLanguageChoices = (choices: Record<string, string>) => {
  try {
    localStorage.setItem(TAG_LANGUAGE_KEY, JSON.stringify(choices));
  } catch (error) {
    console.warn('Could not store the tag language choice', error);
  }
};

const readTagLanguageChoices = (): Record<string, string> => {
  try {
    const stored = JSON.parse(localStorage.getItem(TAG_LANGUAGE_KEY) ?? '{}');
    const choices: Record<string, string> = stored && typeof stored === 'object' ? stored : {};
    const legacy = localStorage.getItem(LEGACY_TAG_LANGUAGE_KEY);
    if (legacy) {
      if (Object.keys(choices).length === 0) {
        choices.sv = legacy;
        writeTagLanguageChoices(choices);
      }
      localStorage.removeItem(LEGACY_TAG_LANGUAGE_KEY);
    }
    return choices;
  } catch {
    return {};
  }
};

export const TagDisplayProvider = ({ children }: { children: ReactNode }) => {
  const { i18n } = useTranslation();
  const systemLanguage = (i18n.resolvedLanguage || i18n.language || 'sv').split('-')[0];
  const [choices, setChoices] = useState<Record<string, string>>(readTagLanguageChoices);
  const [availableTagLanguages, setAvailableTagLanguages] = useState<string[] | undefined>();
  const preferredLanguage = choices[systemLanguage] ?? systemLanguage;
  const tagLanguage = availableTagLanguages?.length && !availableTagLanguages.includes(preferredLanguage) ? availableTagLanguages[0] : preferredLanguage;
  const setTagLanguage = useCallback(
    (lang: string) => {
      const next = { ...choices, [systemLanguage]: lang };
      setChoices(next);
      writeTagLanguageChoices(next);
    },
    [choices, systemLanguage]
  );

  const [highlightedOption, setHighlightedOption] = useState<string>('tagger');
  const [visibilityOption, setVisibilityOption] = useState<string[]>(['core']);
  const [extensionConceptIds, setExtensionConceptIds] = useState<Set<string>>(() => new Set());
  const [tagDataMap, setTagDataMap] = useState<TagDataMap>({});
  const [termHighlightTagIds, setTermHighlightTagIds] = useState<Set<string>>(() => new Set());

  const value = useMemo<TagDisplayContextValue>(
    () => ({
      tagLanguage,
      highlightedOption,
      visibilityOption,
      extensionConceptIds,
      tagDataMap,
      termHighlightTagIds,
      setTagLanguage,
      setAvailableTagLanguages,
      setHighlightedOption,
      setVisibilityOption,
      setExtensionConceptIds,
      setTagDataMap,
      setTermHighlightTagIds,
    }),
    [tagLanguage, highlightedOption, visibilityOption, extensionConceptIds, tagDataMap, termHighlightTagIds, setTagLanguage]
  );

  return <TagDisplayContext.Provider value={value}>{children}</TagDisplayContext.Provider>;
};

export const useTagDisplay = () => useContext(TagDisplayContext);
